import { ref } from "vue";
import type { Tables } from "~/types/types.gen";

export default function useFightCardsDatabase() {
  const client = useSupabaseClient();
  const loading = ref(false);
  const posthog = usePosthog();

  async function fetchFightCards(
    eventId: string,
    fighterId?: string
  ): Promise<{
    data: Tables<"event_fight_cards">[];
    error: unknown;
  }> {
    loading.value = true;

    const query = client
      .from("event_fight_cards")
      .select("*")
      .eq("event_id", eventId);

    if (fighterId) {
      query.or(`fighter_a_id.eq.${fighterId},fighter_b_id.eq.${fighterId}`);
    }

    const { data, error } = await query;

    loading.value = false;

    posthog.logError(error, "useEventsFightCardsDatabase:fetchFightCards");
    return { data: data ?? [], error };
  }

  async function createFightCard(
    payload: Partial<Tables<"event_fight_cards">>
  ): Promise<{
    data: Tables<"event_fight_cards"> | null;
    error: unknown;
  }> {
    try {
      const data = await $fetch<Tables<"event_fight_cards">>(
        "/api/events/fight-cards",
        {
          method: "POST",
          body: payload,
        }
      );

      return { data, error: null };
    } catch (error) {
      posthog.logError(error, "useEventsFightCardsDatabase:createFightCard");
      return { data: null, error };
    }
  }

  async function updateFightCard(
    updates: Partial<Tables<"event_fight_cards">>
  ): Promise<{
    data: Tables<"event_fight_cards"> | null;
    error: unknown;
  }> {
    try {
      const data = await $fetch(`/api/events/fight-cards/${updates.id}`, {
        method: "PATCH",
        body: updates,
      });

      return { data, error: null };
    } catch (error) {
      posthog.logError(error, "useEventsFightCardsDatabase:updateFightCard");
      return { data: null, error };
    }
  }

  async function deleteFightCard(id: string) {
    const { error } = await client
      .from("event_fight_cards")
      .delete()
      .eq("id", id);
    posthog.logError(error, "useEventsFightCardsDatabase:deleteFightCard");
    return { error };
  }

  async function autoGenerateFightCard(
    eventId: string
  ): Promise<Tables<"event_fight_cards">[]> {
    const { data, error } = await client.rpc("generate_fight_card", {
      p_event_id: eventId,
    } as any);

    posthog.logError(
      error,
      "useEventsFightCardsDatabase:autoGenerateFightCard"
    );

    if (error) throw error;

    return data ?? [];
  }

  async function upsertFightCards(
    fightCards: Partial<Tables<"event_fight_cards">>[]
  ): Promise<{
    data: Tables<"event_fight_cards">[] | null;
    error: unknown;
  }> {
    const { data, error } = await client
      .from("event_fight_cards")
      .upsert(fightCards)
      .select();

    posthog.logError(error, "useEventsFightCardsDatabase:upsertFightCards");
    return { data, error };
  }

  return {
    fetchFightCards,
    createFightCard,
    updateFightCard,
    deleteFightCard,
    loading,
    autoGenerateFightCard,
    upsertFightCards,
  };
}
